// What the three proposal channels share: opening a filtered list, a create form filled in, and a clone over
// rows are three different proposals carried the same way, and each of them used to hold its own copy of the
// carrying. Keep it import-free of React, so `node --test` can run the channels' tests against them directly.
//
// Every proposal is CLIENT-BOUND but AGENT-SUPPLIED, so what is kept here is only ever what a channel has
// already checked: this module decides how long a proposal waits and how an offer is remembered, never what
// is in one.

/** Longest string carried out of a proposal, whatever it is a value of. */
export const MAX_VALUE_LENGTH = 200;

const isSafeCharacter = (character: string): boolean => {
  const code = character.charCodeAt(0);
  return code >= 0x20 && code !== 0x7f;
};

/** A string as it can be shown and matched: control characters out, trimmed, capped. */
export const clean = (value: string, maxLength: number): string => [...value].filter(isSafeCharacter).join('').trim().slice(0, maxLength);

/**
 * Whether a path from a proposal is one this app can navigate to at all: a relative path of its own, never
 * another origin and never a scheme. The route it names is checked separately against the app's own registry,
 * so this is the shape check and not the whole of it.
 */
export const isSafePath = (path: string): boolean =>
  path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !/^\/[^/?#]*:/.test(path);

/** The path with its query string dropped, which is the part the app's registries declare. */
export const pathname = (path: string): string => path.split('?')[0].split('#')[0];

/** How long a proposal waits for the page it needs before it is no longer what the user asked for. */
export const PENDING_TTL_MS = 10_000;

/** A proposal waiting for its page, with the deadline the first hold started. */
export interface HeldProposal<Instruction> {
  instruction: Instruction;
  expiresAt: number;
}

/**
 * The proposal behind an offer, kept in the browser rather than sent as the call's arguments: the panel
 * prints a step's arguments under its title, and a user reading `{"amount":4.5}` is reading our plumbing.
 * Keyed by the description the step shows, so a click opens what its own words say and not whatever was
 * offered most recently. `confirmed` is what taking one means on that channel, which is the flag saying the
 * user has agreed to be taken to the page.
 */
export const offerStore = <Instruction>(
  describe: (instruction: Instruction) => string,
  confirmed: (instruction: Instruction) => Instruction,
) => {
  const MAX_REMEMBERED = 20;
  const offers = new Map<string, Instruction>();

  return {
    /** The call the panel offers: one readable argument, with the proposal kept against it. */
    offer: (instruction: Instruction): Record<string, unknown> => {
      const open = describe(instruction);
      if (offers.size >= MAX_REMEMBERED) offers.delete(offers.keys().next().value as string);
      offers.set(open, instruction);
      return { open };
    },

    /** The proposal behind a confirmed call, or null when it is no longer remembered. */
    take: (params: unknown): Instruction | null => {
      const open = (params as { open?: unknown } | null)?.open;
      if (typeof open !== 'string') return null;

      const instruction = offers.get(open);
      if (instruction === undefined) return null;

      offers.delete(open);
      return confirmed(instruction);
    },

    forget: (): void => {
      offers.clear();
    },
  };
};

/**
 * A proposal for a page that is not on screen waits here until that page arrives. It survives the one
 * navigation it takes to get there and nothing more: it expires, it is dropped once taken, and a newer one
 * replaces it.
 */
export const pendingStore = <Instruction extends { entity: string }>() => {
  let pending: HeldProposal<Instruction> | null = null;

  return {
    /**
     * Holds a proposal for a page that is not on screen, replacing any older one. `expiresAt` carries the
     * original deadline when a page puts one back because what it needs is not ready, so waiting stays
     * inside the ten seconds the first hold started.
     */
    hold: (instruction: Instruction, expiresAt?: number): void => {
      pending = { instruction, expiresAt: expiresAt ?? Date.now() + PENDING_TTL_MS };
    },

    /** The proposal waiting for this page, if it is still current, with its deadline. Taking it clears it. */
    take: (entityType: string): HeldProposal<Instruction> | null => {
      if (pending === null) return null;
      if (pending.expiresAt <= Date.now()) {
        pending = null;
        return null;
      }
      if (pending.instruction.entity !== entityType) return null;

      const waiting = pending;
      pending = null;
      return waiting;
    },

    clear: (): void => {
      pending = null;
    },
  };
};
